import type React from 'react';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { cmsPageQuery, useAxiosCmsInstance } from '@gov-nx/api';
import { usePoQuery } from '@gov-nx/api';
import { govLog, useAppContext } from '@gov-nx/core/app';
import { useAppLanguage } from '@gov-nx/core/hooks';
import {
	getI18n,
	i18nLoadNamespaces,
	useCmsPage,
	useConfiguration,
} from '@gov-nx/core/service';
import type { PageCode } from '@gov-nx/module/page';
import { useCmsStore } from '@gov-nx/store';

export interface CmsPageProviderCoreProps {
	children: React.ReactNode;
	code: PageCode;
	loadExtraTranslations?: PageCode[];
	onError?: () => void;
	onNotFound?: () => void;
	skeleton?: React.ReactNode;
}

export const CmsPageProviderCore = forwardRef(
	(props: CmsPageProviderCoreProps, ref) => {
		const { children, code, loadExtraTranslations, skeleton = null } = props;
		const saveCmsPage = useCmsStore('saveCmsPage');
		const [translationLoaded, setTranslationLoaded] = useState(
			getI18n().hasLoadedNamespace(code)
		);
		const page = useCmsPage({ code });
		const { setCurrentCode } = useAppContext();
		const { cmsKeyCache } = useConfiguration();
		const { language } = useAppLanguage();
		const { axiosCmsInstance } = useAxiosCmsInstance();

		const pageQuery = usePoQuery({
			queryKey: ['cms-page', code, cmsKeyCache, language],
			queryFn: () =>
				cmsPageQuery(axiosCmsInstance, { cmsKeyCache, code, locale: language }),
			onSuccess: (data) => saveCmsPage(code, data),
			retry: 0,
			cacheTime: 0,
			refetchOnWindowFocus: false,
		});

		const init = useCallback(async () => {
			await Promise.all([
				i18nLoadNamespaces([code, ...(loadExtraTranslations ?? [])]),
				pageQuery.refetch(),
			]);
			setTranslationLoaded(getI18n().hasLoadedNamespace(code));
			// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		}, [code, pageQuery]);

		useImperativeHandle(ref, () => ({
			init() {
				setCurrentCode(code, null);
				init().finally(() => {
					govLog('Page loaded [' + code + ']');
				});
			},
		}));

		if (page && translationLoaded) {
			return children;
		}
		return skeleton;
	}
);
